// src/pages/TikpagExperience/TikpagExperienceRelatorios.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";

import {
  Award,
  BarChart3,
  Building2,
  CheckCircle2,
  CircleDollarSign,
  Filter,
  Gift,
  Layers3,
  Loader2,
  PackageCheck,
  RefreshCcw,
  Sparkles,
  TrendingUp,
  Trophy,
  Users
} from "lucide-react";

import axios from "../../axiosConfig";

import {
  getPinImage
} from "../../utils/pinAssets";

import "./TikpagExperienceRelatorios.css";

const MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro"
];

const hoje = new Date();

const formatarDinheiro = (valor) =>
  Number(valor || 0).toLocaleString(
    "pt-BR",
    {
      style: "currency",
      currency: "BRL"
    }
  );

const formatarNumero = (valor) =>
  Number(valor || 0).toLocaleString(
    "pt-BR"
  );

const formatarPercentual = (valor) =>
  `${Number(valor || 0).toLocaleString(
    "pt-BR",
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }
  )}%`;

const formatarCnpj = (valor) => {
  const numeros =
    String(valor || "")
      .replace(/\D/g, "");

  if (numeros.length !== 14) {
    return valor || "-";
  }

  return numeros.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5"
  );
};

const nomeCompetencia = (
  ano,
  mes
) =>
  `${MESES[
    Number(mes) - 1
  ] || mes}/${ano}`;

export default function TikpagExperienceRelatorios() {
  const [campanhas, setCampanhas] =
    useState([]);

  const [campanhaId, setCampanhaId] =
    useState("");

  const [
    anoInicio,
    setAnoInicio
  ] = useState(
    hoje.getFullYear()
  );

  const [
    mesInicio,
    setMesInicio
  ] = useState(1);

  const [
    anoFim,
    setAnoFim
  ] = useState(
    hoje.getFullYear()
  );

  const [
    mesFim,
    setMesFim
  ] = useState(
    hoje.getMonth() + 1
  );

  const [
    empresaCnpj,
    setEmpresaCnpj
  ] = useState("");

  const [grupoId, setGrupoId] =
    useState("");

  const [dados, setDados] =
    useState(null);

  const [loading, setLoading] =
    useState(false);

  const [
    loadingCampanhas,
    setLoadingCampanhas
  ] = useState(true);

  const [erro, setErro] =
    useState("");

  const carregarCampanhas =
    useCallback(async () => {
      setLoadingCampanhas(true);
      setErro("");

      try {
        const resposta =
          await axios.get(
            "/admin/experience/campanhas"
          );

        const lista = Array.isArray(
          resposta.data?.campanhas
        )
          ? resposta.data.campanhas
          : [];

        setCampanhas(lista);

        setCampanhaId((atual) => {
          if (atual) return atual;

          const preferida =
            lista.find(
              (item) =>
                item.status ===
                "EM_ANDAMENTO"
            ) ||
            lista.find(
              (item) =>
                item.status ===
                "INSCRICOES"
            ) ||
            lista[0];

          return preferida
            ? String(preferida.id)
            : "";
        });
      } catch (err) {
        setErro(
          err.response?.data?.error ||
            "Não foi possível carregar as campanhas."
        );
      } finally {
        setLoadingCampanhas(false);
      }
    }, []);

  useEffect(() => {
    carregarCampanhas();
  }, [carregarCampanhas]);

  const carregarRelatorio =
    useCallback(async () => {
      if (!campanhaId) return;

      setLoading(true);
      setErro("");

      try {
        const resposta =
          await axios.get(
            "/admin/experience/relatorios/resumo",
            {
              params: {
                campanha_id:
                  Number(campanhaId),

                ano_inicio:
                  Number(anoInicio),

                mes_inicio:
                  Number(mesInicio),

                ano_fim:
                  Number(anoFim),

                mes_fim:
                  Number(mesFim),

                empresa_cnpj:
                  empresaCnpj ||
                  undefined,

                grupo_id:
                  grupoId
                    ? Number(grupoId)
                    : undefined
              }
            }
          );

        setDados(
          resposta.data || null
        );
      } catch (err) {
        console.error(
          "Erro ao carregar relatório Experience:",
          err
        );

        setErro(
          err.response?.data?.error ||
            "Não foi possível carregar o relatório."
        );
      } finally {
        setLoading(false);
      }
    }, [
      campanhaId,
      anoInicio,
      mesInicio,
      anoFim,
      mesFim,
      empresaCnpj,
      grupoId
    ]);

  useEffect(() => {
    carregarRelatorio();
  }, [carregarRelatorio]);

  const limparFiltros = () => {
    setAnoInicio(
      hoje.getFullYear()
    );
    setMesInicio(1);
    setAnoFim(
      hoje.getFullYear()
    );
    setMesFim(
      hoje.getMonth() + 1
    );
    setEmpresaCnpj("");
    setGrupoId("");
  };

  const evolucaoMensal =
    Array.isArray(
      dados?.evolucao_mensal
    )
      ? dados.evolucao_mensal
      : [];

  const distribuicaoPins =
    Array.isArray(
      dados?.distribuicao_pins
    )
      ? dados.distribuicao_pins
      : [];

  const experiencias =
    Array.isArray(
      dados?.experiencias_mais_escolhidas
    )
      ? dados
          .experiencias_mais_escolhidas
      : [];

  const empresas =
    Array.isArray(
      dados?.empresas_maior_volume
    )
      ? dados.empresas_maior_volume
      : [];

  const grupos =
    Array.isArray(
      dados?.grupos_maior_volume
    )
      ? dados.grupos_maior_volume
      : [];

  const filtrosGrupos =
    Array.isArray(
      dados?.filtros?.grupos
    )
      ? dados.filtros.grupos
      : [];

  const filtrosEmpresas =
    Array.isArray(
      dados?.filtros?.empresas
    )
      ? dados.filtros.empresas
      : [];

  const maxVolumeMensal =
    useMemo(
      () =>
        Math.max(
          1,
          ...evolucaoMensal.map(
            (item) =>
              Number(
                item.volume_apurado ||
                  0
              )
          )
        ),
      [evolucaoMensal]
    );

  const maxExperiencias =
    useMemo(
      () =>
        Math.max(
          1,
          ...experiencias.map(
            (item) =>
              Number(
                item
                  .quantidade_escolhas ||
                  0
              )
          )
        ),
      [experiencias]
    );

  const maxEmpresas =
    useMemo(
      () =>
        Math.max(
          1,
          ...empresas.map(
            (item) =>
              Number(
                item.volume_apurado ||
                  0
              )
          )
        ),
      [empresas]
    );

  const cards =
    useMemo(
      () => [
        {
          titulo:
            "Inscritos",

          valor:
            formatarNumero(
              dados?.cards
                ?.participantes_inscritos ||
                0
            ),

          legenda:
            "Participantes ativos",

          Icone:
            Users
        },
        {
          titulo:
            "Com movimentação",

          valor:
            formatarNumero(
              dados?.cards
                ?.participantes_com_movimentacao ||
                0
            ),

          legenda:
            "Participaram no período",

          Icone:
            TrendingUp
        },
        {
          titulo:
            "Volume total",

          valor:
            formatarDinheiro(
              dados?.cards
                ?.volume_total || 0
            ),

          legenda:
            "Volume apurado",

          Icone:
            CircleDollarSign
        },
        {
          titulo:
            "Operações",

          valor:
            formatarNumero(
              dados?.cards
                ?.total_operacoes || 0
            ),

          legenda:
            "Operações aprovadas",

          Icone:
            BarChart3
        },
        {
          titulo:
            "Premiados",

          valor:
            formatarNumero(
              dados?.cards
                ?.participantes_premiados ||
                0
            ),

          legenda:
            "Participantes premiados",

          Icone:
            Trophy
        },
        {
          titulo:
            "Taxa de premiação",

          valor:
            formatarPercentual(
              dados?.cards
                ?.taxa_premiacao || 0
            ),

          legenda:
            "Premiados sobre inscritos",

          Icone:
            Award
        },
        {
          titulo:
            "Experiências escolhidas",

          valor:
            formatarNumero(
              dados?.cards
                ?.experiencias_escolhidas ||
                0
            ),

          legenda:
            "Escolhas registradas",

          Icone:
            Gift
        },
        {
          titulo:
            "PINs entregues",

          valor:
            formatarNumero(
              dados?.cards
                ?.pins_entregues || 0
            ),

          legenda:
            "Entregas concluídas",

          Icone:
            PackageCheck
        }
      ],
      [dados]
    );

  const semApuracoes =
    Number(
      dados?.cards
        ?.participantes_apurados || 0
    ) === 0;

  const funilEscolhas =
    dados?.funil_escolhas || {};

  const situacaoPins =
    dados?.situacao_pins_fisicos ||
    {};

  return (
    <div className="experienceRelatorios-page">
      <header className="experienceRelatorios-header">
        <div>
          <span className="experienceRelatorios-kicker">
            Tikpag Experience
          </span>

          <h1>
            Relatórios e indicadores
          </h1>

          <p>
            Analise adesão, desempenho,
            premiação, escolhas e logística
            em uma única visão.
          </p>
        </div>

        <button
          type="button"
          className="experienceRelatorios-refreshButton"
          onClick={carregarRelatorio}
          disabled={
            loading ||
            loadingCampanhas
          }
        >
          <RefreshCcw
            size={16}
            className={
              loading
                ? "experienceRelatorios-spin"
                : ""
            }
          />

          Atualizar
        </button>
      </header>

      {erro && (
        <div className="experienceRelatorios-alert erro">
          {erro}
        </div>
      )}

      <section className="experienceRelatorios-toolbar">
        <label className="experienceRelatorios-field campanha">
          <span>Campanha</span>

          <select
            value={campanhaId}
            onChange={(event) =>
              setCampanhaId(
                event.target.value
              )
            }
            disabled={loadingCampanhas}
          >
            {!campanhas.length && (
              <option value="">
                Nenhuma campanha
              </option>
            )}

            {campanhas.map(
              (campanha) => (
                <option
                  key={campanha.id}
                  value={campanha.id}
                >
                  {campanha.nome}
                </option>
              )
            )}
          </select>
        </label>

        <label className="experienceRelatorios-field">
          <span>Ano inicial</span>

          <input
            type="number"
            min="2020"
            max="2100"
            value={anoInicio}
            onChange={(event) =>
              setAnoInicio(
                Number(
                  event.target.value
                )
              )
            }
          />
        </label>

        <label className="experienceRelatorios-field">
          <span>Mês inicial</span>

          <select
            value={mesInicio}
            onChange={(event) =>
              setMesInicio(
                Number(
                  event.target.value
                )
              )
            }
          >
            {MESES.map(
              (nome, indice) => (
                <option
                  key={nome}
                  value={indice + 1}
                >
                  {nome}
                </option>
              )
            )}
          </select>
        </label>

        <label className="experienceRelatorios-field">
          <span>Ano final</span>

          <input
            type="number"
            min="2020"
            max="2100"
            value={anoFim}
            onChange={(event) =>
              setAnoFim(
                Number(
                  event.target.value
                )
              )
            }
          />
        </label>

        <label className="experienceRelatorios-field">
          <span>Mês final</span>

          <select
            value={mesFim}
            onChange={(event) =>
              setMesFim(
                Number(
                  event.target.value
                )
              )
            }
          >
            {MESES.map(
              (nome, indice) => (
                <option
                  key={nome}
                  value={indice + 1}
                >
                  {nome}
                </option>
              )
            )}
          </select>
        </label>

        <label className="experienceRelatorios-field empresa">
          <span>Empresa</span>

          <select
            value={empresaCnpj}
            onChange={(event) =>
              setEmpresaCnpj(
                event.target.value
              )
            }
          >
            <option value="">
              Todas as empresas
            </option>

            {filtrosEmpresas.map(
              (empresa) => (
                <option
                  key={empresa.cnpj}
                  value={empresa.cnpj}
                >
                  {empresa.nome}
                </option>
              )
            )}
          </select>
        </label>

        <label className="experienceRelatorios-field grupo">
          <span>Grupo empresarial</span>

          <select
            value={grupoId}
            onChange={(event) =>
              setGrupoId(
                event.target.value
              )
            }
          >
            <option value="">
              Todos os grupos
            </option>

            {filtrosGrupos.map(
              (grupo) => (
                <option
                  key={grupo.id}
                  value={grupo.id}
                >
                  {grupo.nome}
                </option>
              )
            )}
          </select>
        </label>

        <button
          type="button"
          className="experienceRelatorios-clearButton"
          onClick={limparFiltros}
        >
          <Filter size={15} />
          Limpar
        </button>
      </section>

      <section className="experienceRelatorios-cards">
        {cards.map(
          ({
            titulo,
            valor,
            legenda,
            Icone
          }) => (
            <article
              key={titulo}
              className="experienceRelatorios-card"
            >
              <div>
                <Icone size={20} />
              </div>

              <span>{titulo}</span>
              <strong>{valor}</strong>
              <small>{legenda}</small>
            </article>
          )
        )}
      </section>

      {semApuracoes &&
        !loading && (
          <section className="experienceRelatorios-emptyNotice">
            <Sparkles size={27} />

            <div>
              <h2>
                Período ainda sem apuração
              </h2>

              <p>
                Os indicadores financeiros e de
                premiação aparecerão após o
                fechamento das competências.
              </p>
            </div>
          </section>
        )}

      <section className="experienceRelatorios-mainGrid">
        <article className="experienceRelatorios-panel evolution">
          <header>
            <div>
              <span>
                Evolução mensal
              </span>

              <h2>
                Volume por competência
              </h2>
            </div>
          </header>

          {evolucaoMensal.length ? (
            <div className="experienceRelatorios-evolutionList">
              {evolucaoMensal.map(
                (item) => {
                  const percentual =
                    Math.max(
                      3,
                      (
                        Number(
                          item.volume_apurado ||
                            0
                        ) /
                        maxVolumeMensal
                      ) * 100
                    );

                  return (
                    <div
                      key={`${item.ano}-${item.mes}`}
                      className="experienceRelatorios-evolutionItem"
                    >
                      <div className="experienceRelatorios-evolutionTop">
                        <strong>
                          {nomeCompetencia(
                            item.ano,
                            item.mes
                          )}
                        </strong>

                        <span>
                          {formatarDinheiro(
                            item.volume_apurado
                          )}
                        </span>
                      </div>

                      <div className="experienceRelatorios-track">
                        <div
                          style={{
                            width:
                              `${percentual}%`
                          }}
                        />
                      </div>

                      <small>
                        {formatarNumero(
                          item.quantidade_operacoes
                        )}{" "}
                        operações •{" "}
                        {formatarNumero(
                          item.participantes_premiados
                        )}{" "}
                        premiados
                      </small>
                    </div>
                  );
                }
              )}
            </div>
          ) : (
            <div className="experienceRelatorios-emptyPanel">
              Nenhum dado mensal no período.
            </div>
          )}
        </article>

        <article className="experienceRelatorios-panel pins">
          <header>
            <div>
              <span>
                Distribuição de PINs
              </span>

              <h2>
                Premiações por nível
              </h2>
            </div>
          </header>

          {distribuicaoPins.length ? (
            <div className="experienceRelatorios-pinGrid">
              {distribuicaoPins.map(
                (pin) => {
                  const imagem =
                    getPinImage({
                      codigo:
                        pin.pin_codigo
                    });

                  return (
                    <div
                      key={pin.pin_id}
                      className="experienceRelatorios-pinItem"
                    >
                      <div>
                        {imagem && (
                          <img
                            src={imagem}
                            alt={
                              pin.pin_nome
                            }
                          />
                        )}
                      </div>

                      <strong>
                        {formatarNumero(
                          pin.quantidade
                        )}
                      </strong>

                      <span>
                        {pin.pin_nome}
                      </span>

                      <small>
                        {formatarDinheiro(
                          pin.volume_total
                        )}
                      </small>
                    </div>
                  );
                }
              )}
            </div>
          ) : (
            <div className="experienceRelatorios-emptyPanel">
              Nenhum PIN distribuído no
              período.
            </div>
          )}
        </article>

        <article className="experienceRelatorios-panel funnel">
          <header>
            <div>
              <span>
                Funil de experiências
              </span>

              <h2>
                Jornada das escolhas
              </h2>
            </div>
          </header>

          <div className="experienceRelatorios-funnelList">
            {[
              [
                "Aguardando escolha",
                funilEscolhas
                  .aguardando_escolha
              ],
              [
                "Confirmadas",
                funilEscolhas.confirmadas
              ],
              [
                "Em processamento",
                funilEscolhas
                  .em_processamento
              ],
              [
                "Disponibilizadas",
                funilEscolhas
                  .disponibilizadas
              ],
              [
                "Utilizadas",
                funilEscolhas.utilizadas
              ],
              [
                "Expiradas",
                funilEscolhas.expiradas
              ],
              [
                "Canceladas",
                funilEscolhas.canceladas
              ]
            ].map(
              ([rotulo, valor]) => (
                <div key={rotulo}>
                  <span>{rotulo}</span>

                  <strong>
                    {formatarNumero(
                      valor || 0
                    )}
                  </strong>
                </div>
              )
            )}
          </div>
        </article>

        <article className="experienceRelatorios-panel logistics">
          <header>
            <div>
              <span>
                Logística física
              </span>

              <h2>
                Situação dos PINs
              </h2>
            </div>
          </header>

          <div className="experienceRelatorios-logisticsGrid">
            {[
              [
                "Pendentes",
                situacaoPins.pendentes
              ],
              [
                "Em produção",
                situacaoPins.em_producao
              ],
              [
                "Prontos",
                situacaoPins.prontos
              ],
              [
                "Enviados",
                situacaoPins.enviados
              ],
              [
                "Entregues",
                situacaoPins.entregues
              ],
              [
                "Retirados",
                situacaoPins.retirados
              ]
            ].map(
              ([rotulo, valor]) => (
                <div key={rotulo}>
                  <span>{rotulo}</span>

                  <strong>
                    {formatarNumero(
                      valor || 0
                    )}
                  </strong>
                </div>
              )
            )}
          </div>
        </article>
      </section>

      <section className="experienceRelatorios-secondaryGrid">
        <article className="experienceRelatorios-panel experiences">
          <header>
            <div>
              <span>
                Experiências
              </span>

              <h2>
                Mais escolhidas
              </h2>
            </div>
          </header>

          {experiencias.length ? (
            <div className="experienceRelatorios-rankingList">
              {experiencias.map(
                (
                  experiencia,
                  indice
                ) => {
                  const percentual =
                    Math.max(
                      4,
                      (
                        Number(
                          experiencia
                            .quantidade_escolhas ||
                            0
                        ) /
                        maxExperiencias
                      ) * 100
                    );

                  return (
                    <div
                      key={
                        experiencia
                          .experiencia_id
                      }
                      className="experienceRelatorios-rankingRow"
                    >
                      <span>
                        {indice + 1}º
                      </span>

                      <div>
                        <strong>
                          {
                            experiencia.titulo
                          }
                        </strong>

                        <small>
                          {experiencia.fornecedor ||
                            "Fornecedor não informado"}
                        </small>

                        <div className="experienceRelatorios-miniTrack">
                          <div
                            style={{
                              width:
                                `${percentual}%`
                            }}
                          />
                        </div>
                      </div>

                      <strong>
                        {formatarNumero(
                          experiencia
                            .quantidade_escolhas
                        )}
                      </strong>
                    </div>
                  );
                }
              )}
            </div>
          ) : (
            <div className="experienceRelatorios-emptyPanel">
              Nenhuma experiência escolhida.
            </div>
          )}
        </article>

        <article className="experienceRelatorios-panel companies">
          <header>
            <div>
              <span>
                Empresas
              </span>

              <h2>
                Maior volume
              </h2>
            </div>
          </header>

          {empresas.length ? (
            <div className="experienceRelatorios-rankingList">
              {empresas.map(
                (empresa) => {
                  const percentual =
                    Math.max(
                      4,
                      (
                        Number(
                          empresa.volume_apurado ||
                            0
                        ) /
                        maxEmpresas
                      ) * 100
                    );

                  return (
                    <div
                      key={`${empresa.empresa_cnpj}-${empresa.posicao}`}
                      className="experienceRelatorios-rankingRow"
                    >
                      <span>
                        {empresa.posicao}º
                      </span>

                      <div>
                        <strong>
                          {
                            empresa.empresa_nome
                          }
                        </strong>

                        <small>
                          {formatarCnpj(
                            empresa.empresa_cnpj
                          )}{" "}
                          •{" "}
                          {formatarNumero(
                            empresa.participantes
                          )}{" "}
                          participantes
                        </small>

                        <div className="experienceRelatorios-miniTrack">
                          <div
                            style={{
                              width:
                                `${percentual}%`
                            }}
                          />
                        </div>
                      </div>

                      <strong>
                        {formatarDinheiro(
                          empresa.volume_apurado
                        )}
                      </strong>
                    </div>
                  );
                }
              )}
            </div>
          ) : (
            <div className="experienceRelatorios-emptyPanel">
              Nenhuma empresa apurada no
              período.
            </div>
          )}
        </article>
      </section>

      <section className="experienceRelatorios-tablePanel">
        <header>
          <div>
            <span>
              Grupos empresariais
            </span>

            <h2>
              Desempenho consolidado
            </h2>
          </div>
        </header>

        <div className="experienceRelatorios-tableWrapper">
          <table>
            <thead>
              <tr>
                <th>Posição</th>
                <th>Grupo</th>
                <th>Empresas</th>
                <th>Participantes</th>
                <th>Operações</th>
                <th>Volume</th>
                <th>Premiações</th>
              </tr>
            </thead>

            <tbody>
              {grupos.map(
                (grupo) => (
                  <tr key={grupo.grupo_id}>
                    <td>
                      <span className="experienceRelatorios-position">
                        {grupo.posicao}º
                      </span>
                    </td>

                    <td>
                      <strong>
                        {grupo.grupo_nome}
                      </strong>
                    </td>

                    <td>
                      {formatarNumero(
                        grupo.empresas
                      )}
                    </td>

                    <td>
                      {formatarNumero(
                        grupo.participantes
                      )}
                    </td>

                    <td>
                      {formatarNumero(
                        grupo.quantidade_operacoes
                      )}
                    </td>

                    <td>
                      <strong>
                        {formatarDinheiro(
                          grupo.volume_apurado
                        )}
                      </strong>
                    </td>

                    <td>
                      {formatarNumero(
                        grupo.premiacoes
                      )}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>

          {!loading &&
            grupos.length === 0 && (
              <div className="experienceRelatorios-emptyPanel large">
                Nenhum grupo empresarial apurado
                no período.
              </div>
            )}

          {loading && (
            <div className="experienceRelatorios-loading">
              <Loader2
                size={26}
                className="experienceRelatorios-spin"
              />

              <span>
                Carregando relatório...
              </span>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}